// src/pages/PerformanceAssessments/ManagerPerformanceOverview.js
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    CAccordion,
    CAccordionBody,
    CAccordionHeader,
    CAccordionItem,
    CAlert,
    CBadge,
    CButton,
    CCard,
    CCardBody,
    CCardHeader,
    CCol,
    CFormSelect,
    CRow,
    CSpinner,
    CTable,
    CTableBody,
    CTableHead,
    CTableHeaderCell,
    CTableRow,
    CTableDataCell,
    CListGroup,
    CListGroupItem,
    CModal,
    CModalHeader,
    CModalTitle,
    CModalBody,
    CModalFooter,
    CForm,
    CFormTextarea,
    CLink,
    CTooltip,
} from '@coreui/react';

import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { apiClient } from 'services/Api';
import ToastNotification from 'components/globals/ToastNotification';
import { formatScore, AVAILABLE_YEARS, ROLE_LABELS } from './questions';

// ---------------- Modal ----------------

const PerformanceMeetingModal = ({ visible, onClose, context }) => {
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [toast, setToast] = useState(null);

    const availableYears = useMemo(() => AVAILABLE_YEARS, []);

    const [payload, setPayload] = useState(null);

    // editable fields
    const [suggestions, setSuggestions] = useState('');
    const [observations, setObservations] = useState('');

    const fetchPack = useCallback(async () => {
        if (!context?.year || !context?.department_id || !context?.evaluatee_id) return;

        setLoading(true);
        try {
            const res = await apiClient.get(
                `/performance-assessments/meeting-pack/?year=${context.year}&department_id=${context.department_id}&evaluatee_id=${context.evaluatee_id}`
            );

            const data = res.data || null;
            setPayload(data);

            // prefill from meeting object (if present)
            const meeting = data?.meeting || {};
            setSuggestions(meeting.suggestions || '');
            setObservations(meeting.observations || '');
        } catch (err) {
            setPayload(null);
            setToast({
                title: 'Avaliação de Desempenho',
                message: 'Erro ao carregar detalhe da reunião.',
                type: 'error',
            });
        } finally {
            setLoading(false);
        }
    }, [context]);

    useEffect(() => {
        if (visible) fetchPack();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible]);

    const handleSave = async (e) => {
        e.preventDefault();
        if (!payload?.meeting?.id) return;

        setSaving(true);
        try {
            const body = {
                id: payload.meeting.id,
                suggestions,
                observations,
            };

            await apiClient.post('/performance-assessments/meeting-pack/', body);

            setToast({
                title: 'Avaliação de Desempenho',
                message: 'Registo da reunião guardado com sucesso.',
                type: 'success',
            });

            await fetchPack();
        } catch (err) {
            setToast({
                title: 'Avaliação de Desempenho',
                message: 'Erro ao guardar o registo da reunião.',
                type: 'error',
            });
        } finally {
            setSaving(false);
        }
    };

    const evaluateeName = context?.evaluatee_name || payload?.meeting?.evaluatee_name || '—';
    const deptName = context?.department_name || payload?.meeting?.department_name || '—';

    const weak = Array.isArray(payload?.weak_points) ? payload.weak_points : [];
    const strong = Array.isArray(payload?.strong_points) ? payload.strong_points : [];
    const evaluatorObs = Array.isArray(payload?.evaluator_observations) ? payload.evaluator_observations : [];

    return (
        <>
            <ToastNotification toast={toast} setToast={setToast} />

            <CModal visible={visible} onClose={onClose} alignment="center" size="xl" scrollable backdrop="static">
                <CModalHeader>
                    <CModalTitle>
                        Reunião — {evaluateeName} ({deptName}, {context?.year})
                    </CModalTitle>
                </CModalHeader>

                <CModalBody>
                    {loading ? (
                        <div className="text-center my-3">
                            <CSpinner size="sm" /> A carregar…
                        </div>
                    ) : !payload ? (
                        <CAlert color="danger">Não foi possível carregar os dados.</CAlert>
                    ) : (
                        <>
                            {/* Strong / Weak by group */}
                            <CRow className="g-3 mb-3">
                                <CCol md={6}>
                                    <CCard>
                                        <CCardHeader>
                                            <strong>Pontos fortes</strong>
                                            <span className="text-muted ms-2">(média ≥ 4)</span>
                                        </CCardHeader>
                                        <CCardBody>
                                            {strong.length === 0 ? (
                                                <span className="text-muted">—</span>
                                            ) : (
                                                <CListGroup flush>
                                                    {strong.map((p) => (
                                                        <CListGroupItem
                                                            key={p.group}
                                                            className="d-flex justify-content-between align-items-center"
                                                        >
                                                            <span>{p.label || p.group}</span>
                                                        </CListGroupItem>
                                                    ))}
                                                </CListGroup>
                                            )}
                                        </CCardBody>
                                    </CCard>
                                </CCol>

                                <CCol md={6}>
                                    <CCard>
                                        <CCardHeader>
                                            <strong>Pontos fracos</strong>
                                            <span className="text-muted ms-2">(média &lt; 3)</span>
                                        </CCardHeader>
                                        <CCardBody>
                                            {weak.length === 0 ? (
                                                <span className="text-muted">—</span>
                                            ) : (
                                                <CListGroup flush>
                                                    {weak.map((p) => (
                                                        <CListGroupItem
                                                            key={p.group}
                                                            className="d-flex justify-content-between align-items-center"
                                                        >
                                                            <span>{p.label || p.group}</span>
                                                        </CListGroupItem>
                                                    ))}
                                                </CListGroup>
                                            )}
                                        </CCardBody>
                                    </CCard>
                                </CCol>
                            </CRow>

                            {/* Evaluators observations */}
                            <CCard className="mb-3">
                                <CCardHeader>
                                    <strong>Observações dos avaliadores</strong>
                                </CCardHeader>
                                <CCardBody>
                                    {evaluatorObs.length === 0 ? (
                                        <span className="text-muted">—</span>
                                    ) : (
                                        <CListGroup flush>
                                            {evaluatorObs.map((o) => (
                                                <CListGroupItem key={o.evaluator_id}>
                                                    <div className="d-flex justify-content-between align-items-center">
                                                        <strong>{o.evaluator_name || '—'}</strong>
                                                    </div>
                                                    <div className="mt-2" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                                        {o.text ? o.text : <span className="text-muted">Sem observações.</span>}
                                                    </div>
                                                </CListGroupItem>
                                            ))}
                                        </CListGroup>
                                    )}
                                </CCardBody>
                            </CCard>

                            {/* Meeting fields */}
                            <CCard>
                                <CCardHeader>
                                    <strong>Registo da reunião</strong>
                                </CCardHeader>
                                <CCardBody>
                                    <CForm onSubmit={handleSave}>
                                        <CRow className="g-3">
                                            <CCol md={12}>
                                                <label className="form-label fw-semibold">Sugestões</label>
                                                <CFormTextarea
                                                    rows={4}
                                                    value={suggestions}
                                                    onChange={(e) => setSuggestions(e.target.value)}
                                                />
                                            </CCol>

                                            <CCol md={12}>
                                                <label className="form-label fw-semibold">Observações</label>
                                                <CFormTextarea
                                                    rows={4}
                                                    value={observations}
                                                    onChange={(e) => setObservations(e.target.value)}
                                                />
                                            </CCol>
                                        </CRow>

                                        <div className="d-flex justify-content-end mt-3">
                                            <CButton color="primary" type="submit" disabled={saving}>
                                                {saving ? <CSpinner size="sm" /> : 'Guardar'}
                                            </CButton>
                                        </div>
                                    </CForm>
                                </CCardBody>
                            </CCard>
                        </>
                    )}
                </CModalBody>

                <CModalFooter>
                    <CButton color="secondary" variant="ghost" onClick={onClose}>
                        Fechar
                    </CButton>
                </CModalFooter>
            </CModal>
        </>
    );
};

// ---------------- Page ----------------

const ManagerPerformanceOverview = ({ selectedYear, setSelectedYear }) => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingGenerate, setLoadingGenerate] = useState(false);
    const [toast, setToast] = useState(null);

    const [meetingOpen, setMeetingOpen] = useState(false);
    const [meetingCtx, setMeetingCtx] = useState(null);

    // Generate modal state
    const [generateOpen, setGenerateOpen] = useState(false);
    const [generateYear, setGenerateYear] = useState(new Date().getFullYear());

    const availableYears = useMemo(() => AVAILABLE_YEARS, []);
    const [functionals, setFunctionals] = useState(null);

    useEffect(() => {
        setGenerateYear(selectedYear);
    }, [selectedYear]);

    const openMeeting = (ev) => {
        setMeetingCtx({
            year: ev.year,
            department_id: ev.department_id,
            department_name: ev.department_name,
            evaluatee_id: ev.evaluatee_id,
            evaluatee_name: ev.evaluatee_name,
        });
        setMeetingOpen(true);
    };

    const closeMeeting = () => {
        setMeetingOpen(false);
        setMeetingCtx(null);
    };

    const handleGenerate = () => {
        setGenerateYear(selectedYear || new Date().getFullYear());
        setGenerateOpen(true);
    };

    const fetchOverview = useCallback(async (year) => {
        setLoading(true);
        try {
            const response = await apiClient.get(`/performance-assessments/manager-overview/?year=${year}`);
            const data = response.data || {};

            setRows(Array.isArray(data.rows) ? data.rows : []);
            setFunctionals(typeof data.functionals === 'number' ? data.functionals : null);
        } catch (error) {
            setRows([]);
            setFunctionals(null);
        } finally {
            setLoading(false);
        }
    }, []);

    const handleConfirmGenerate = async (e) => {
        if (e?.preventDefault) e.preventDefault();

        const year = Number(generateYear);

        if (!Number.isInteger(year) || !availableYears.includes(year)) {
            setToast({
                title: 'Avaliação de Desempenho',
                message: 'Ano inválido.',
                type: 'error',
            });
            return;
        }

        setLoadingGenerate(true);
        try {
            await apiClient.post('/performance-assessments/generate/', { year });

            await fetchOverview(year);
            setSelectedYear(year);

            setGenerateOpen(false);
        } catch (error) {
            setToast({
                title: 'Avaliação de Desempenho',
                message: 'Erro ao gerar as avaliações.',
                type: 'error',
            });
        } finally {
            setLoadingGenerate(false);
        }
    };

    useEffect(() => {
        fetchOverview(selectedYear);
    }, [selectedYear, fetchOverview]);

    // ---------------- NEW grouping: by evaluatee ----------------
    const evaluatees = useMemo(() => {
        const map = {};
        rows.forEach((r) => {
            const key = r.evaluatee_id;
            if (!key) return;

            if (!map[key]) {
                map[key] = {
                    evaluatee_id: r.evaluatee_id,
                    evaluatee_name: r.evaluatee_name || '—',
                    items: [],
                };
            }
            map[key].items.push(r);
        });

        const list = Object.values(map);

        // stable ordering (name)
        list.sort((a, b) => (a.evaluatee_name || '').localeCompare(b.evaluatee_name || ''));

        // sort dept rows inside each evaluatee
        list.forEach((x) => {
            x.items.sort((a, b) => (a.department_name || '').localeCompare(b.department_name || ''));
        });

        return list;
    }, [rows]);

    // --------- helpers for the 3 cards (per evaluatee) ---------
    const computeWeightedAverage = useCallback((items) => {
        // Prefer backend value (weighted_final_score) when present; fallback to final_score * weight/100
        const nums = items
            .map((r) => {
                if (!r.complete) return null;
                if (typeof r.weighted_final_score === 'number') return r.weighted_final_score;

                const w = Number(r.department_weight);
                const fs = Number(r.final_score);
                if (Number.isNaN(w) || Number.isNaN(fs)) return null;
                return (fs * w) / 100;
            })
            .filter((v) => typeof v === 'number' && !Number.isNaN(v));

        if (!nums.length) return null;

        // This is the weighted SUM across departments (because each row is already "score * weight/100")
        const sum = nums.reduce((acc, v) => acc + v, 0);
        return sum;
    }, []);

    const computeFinalEvaluation = (weightedAvg, functionals) => {
        if (weightedAvg === null || weightedAvg === undefined) return null;
        if (functionals === null || functionals === undefined) return null;
        const w = Number(weightedAvg);
        const f = Number(functionals);
        if (Number.isNaN(w) || Number.isNaN(f)) return null;
        return w / 2 + f / 2;
    };

    const formatPercent = (v) => {
        const n = Number(v);
        if (Number.isNaN(n)) return '—';
        return `${n}%`;
    };

    const allConcludedForYear = useMemo(() => {
        if (!rows.length) return false;
        return rows.every((r) => r.complete);
    }, [rows]);

    const chartData = useMemo(() => {
        if (!allConcludedForYear) {
            return {
                roleCategories: [],
                scatterPoints: [],
                averageLine: [],
                histogramCategories: [],
                histogramSeries: [],
                hasScatterData: false,
                hasHistogramData: false,
            };
        }

        const orderedRoles = Object.keys(ROLE_LABELS).filter((role) => role !== 'diretor' && role !== 'gerente');
        const roleBuckets = orderedRoles.reduce((acc, role) => {
            acc[role] = [];
            return acc;
        }, {});
        const scatterScores = [];
        const histogramScores = [];

        evaluatees.forEach((ev) => {
            const weightedAvg = computeWeightedAverage(ev.items);
            if (weightedAvg === null || weightedAvg === undefined || Number.isNaN(weightedAvg)) return;
            histogramScores.push(Number(weightedAvg));
        });

        rows.forEach((row) => {
            if (!row?.complete) return;
            const roleKey = row.role || '—';
            if (roleKey === 'diretor' || roleKey === 'gerente' || !roleBuckets[roleKey]) return;
            const numericScore = Number(row.final_score);
            if (Number.isNaN(numericScore)) return;
            scatterScores.push(numericScore);
            roleBuckets[roleKey].push(numericScore);
        });

        const roleCategories = orderedRoles.map((role) => ROLE_LABELS[role] || role);

        const scatterPoints = [];
        const averageLine = orderedRoles.map((role, roleIndex) => {
            const bucket = roleBuckets[role] || [];
            const roleLabel = ROLE_LABELS[role] || role;
            if (bucket.length === 0) {
                return { x: roleIndex, y: null, roleLabel };
            }

            bucket.forEach((value, idx) => {
                scatterPoints.push({
                    x: roleIndex,
                    y: Number(value),
                    roleLabel,
                });
            });

            const avg = bucket.reduce((acc, v) => acc + v, 0) / bucket.length;
            return { x: roleIndex, y: Number(avg.toFixed(4)), roleLabel };
        });

        const BIN_START = 1;
        const BIN_END = 5;
        const BIN_STEP = 0.4;
        const binCount = Math.round((BIN_END - BIN_START) / BIN_STEP);
        const histogramCategories = [];
        const histogramSeries = new Array(binCount).fill(0);

        for (let i = 0; i < binCount; i += 1) {
            const from = BIN_START + BIN_STEP * i;
            const to = from + BIN_STEP;
            histogramCategories.push(`${from.toFixed(1)}–${to.toFixed(1)}`);
        }

        const scaledStart = BIN_START * 100;
        const scaledStep = Math.round(BIN_STEP * 100);

        histogramScores.forEach((value) => {
            if (value === null || value === undefined || Number.isNaN(value)) return;
            const clamped = Math.min(Math.max(value, BIN_START), BIN_END);
            const normalized = Math.round(clamped * 100) / 100;
            const scaledValue = Math.round(normalized * 100);
            let idx = Math.floor((scaledValue - scaledStart) / scaledStep);
            if (idx < 0) idx = 0;
            if (idx >= binCount) idx = binCount - 1;
            histogramSeries[idx] += 1;
        });

        return {
            roleCategories,
            scatterPoints,
            averageLine,
            histogramCategories,
            histogramSeries,
            hasScatterData: scatterScores.length > 0,
            hasHistogramData: histogramScores.length > 0,
        };
    }, [allConcludedForYear, rows, evaluatees, computeWeightedAverage]);

    const scatterOptions = useMemo(() => {
        if (!chartData.hasScatterData) return null;

        return {
            chart: {
                type: 'scatter',
                height: 360,
            },
            title: {
                text: 'Avaliação por função',
            },
            xAxis: {
                categories: chartData.roleCategories,
            },
            yAxis: {
                min: 1,
                max: 5,
                tickInterval: 0.5,
                title: { text: 'Nota final' },
            },
            tooltip: {
                useHTML: true,
                formatter() {
                    const label = this.point?.roleLabel || this.series.name;
                    const value = typeof this.y === 'number' ? this.y.toFixed(2) : '—';
                    const prefix = this.series.type === 'line' ? 'Média' : 'Valor';
                    return `<strong>${label}</strong><br/>${prefix}: ${value}`;
                },
            },
            plotOptions: {
                scatter: {
                    marker: {
                        radius: 4,
                        symbol: 'circle',
                    },
                },
                line: {
                    marker: {
                        enabled: true,
                        radius: 3,
                    },
                },
            },
            legend: {
                enabled: true,
            },
            credits: { enabled: false },
            series: [
                {
                    type: 'scatter',
                    name: 'Colaboradores',
                    data: chartData.scatterPoints,
                    color: '#9e9e9e',
                    showInLegend: false,
                },
                {
                    type: 'line',
                    name: 'Média por função',
                    data: chartData.averageLine,
                    color: '#1f77b4',
                    connectNulls: true,
                },
            ],
        };
    }, [chartData]);

    const histogramOptions = useMemo(() => {
        if (!chartData.hasHistogramData) return null;

        return {
            chart: {
                type: 'column',
                height: 320,
            },
            title: {
                text: 'Avaliação por intervalos',
            },
            xAxis: {
                categories: chartData.histogramCategories,
                title: { text: 'Média final' },
                tickmarkPlacement: 'on',
            },
            yAxis: {
                title: { text: 'Nº de colaboradores' },
                allowDecimals: false,
            },
            tooltip: {
                pointFormat: '<strong>{point.y}</strong> colaboradores',
            },
            plotOptions: {
                column: {
                    pointPadding: 0.1,
                    groupPadding: 0.05,
                },
            },
            legend: {
                enabled: false,
            },
            credits: { enabled: false },
            series: [
                {
                    name: 'Colaboradores',
                    data: chartData.histogramSeries,
                    color: '#74c476',
                },
            ],
        };
    }, [chartData]);

    return (
        <>
            <ToastNotification toast={toast} setToast={setToast} />

            {/* Generate Modal */}
            <CModal visible={generateOpen} onClose={() => setGenerateOpen(false)} alignment="center" backdrop="static">
                <CModalHeader>
                    <CModalTitle>Gerar Avaliações</CModalTitle>
                </CModalHeader>

                <CForm onSubmit={handleConfirmGenerate}>
                    <CModalBody>
                        <label className="form-label fw-semibold">Ano</label>
                        <CFormSelect
                            value={generateYear}
                            onChange={(e) => setGenerateYear(Number(e.target.value))}
                            disabled={loadingGenerate}
                        >
                            {availableYears.map((y) => (
                                <option key={`gen-${y}`} value={y}>
                                    {y}
                                </option>
                            ))}
                        </CFormSelect>
                        <div className="text-muted mt-2" style={{ fontSize: 13 }}>
                            Indique o ano para o qual pretende gerar as avaliações.
                        </div>
                    </CModalBody>

                    <CModalFooter>
                        <CButton
                            color="secondary"
                            variant="ghost"
                            onClick={() => setGenerateOpen(false)}
                            disabled={loadingGenerate}
                        >
                            Cancelar
                        </CButton>
                        <CButton color="info" className="text-white" type="submit" disabled={loadingGenerate}>
                            {loadingGenerate ? <CSpinner size="sm" /> : 'Gerar'}
                        </CButton>
                    </CModalFooter>
                </CForm>
            </CModal>

            <PerformanceMeetingModal visible={meetingOpen} onClose={closeMeeting} context={meetingCtx} />

            <CCard className="mb-4">
                {/* DO NOT CHANGE HEADER */}
                <CCardHeader className="d-flex justify-content-between align-items-center">
                    <h4 className="mb-0">Visão Global de Desempenho por Colaborador</h4>

                    <CButton color="info" className="text-white" onClick={handleGenerate} disabled={loadingGenerate}>
                        {loadingGenerate ? <CSpinner size="sm" /> : 'Gerar Avaliações'}
                    </CButton>
                </CCardHeader>

                <CCardBody>
                    {loading ? (
                        <div className="text-center my-3">
                            <CSpinner size="sm" /> A carregar…
                        </div>
                    ) : evaluatees.length === 0 ? (
                        <CAlert color="info">Não existem avaliações para o ano seleccionado.</CAlert>
                    ) : (
                        <>
                            {allConcludedForYear ? (
                                chartData.hasScatterData || chartData.hasHistogramData ? (
                                    <CRow className="g-3 mb-4">
                                        {chartData.hasScatterData && (
                                            <CCol lg={12}>
                                                <CCard className="h-100">
                                                    <CCardBody>
                                                        <HighchartsReact highcharts={Highcharts} options={scatterOptions} />
                                                    </CCardBody>
                                                </CCard>
                                            </CCol>
                                        )}
                                        {chartData.hasHistogramData && (
                                            <CCol lg={12}>
                                                <CCard className="h-100">
                                                    <CCardBody>
                                                        <HighchartsReact highcharts={Highcharts} options={histogramOptions} />
                                                    </CCardBody>
                                                </CCard>
                                            </CCol>
                                        )}
                                    </CRow>
                                ) : (
                                    <CAlert color="info" className="mb-4">
                                        Sem dados suficientes para apresentar os gráficos.
                                    </CAlert>
                                )
                            ) : (
                                <CAlert color="warning" className="mb-4">
                                    Os gráficos ficam disponíveis apenas quando todas as avaliações do ano estiverem concluídas.
                                </CAlert>
                            )}

                            <CAccordion>
                                {evaluatees.map((ev) => {
                                    const weightedAvg = computeWeightedAverage(ev.items);
                                    const finalEvaluation = computeFinalEvaluation(weightedAvg, functionals);
                                    
                                    return (
                                        <CAccordionItem key={ev.evaluatee_id} itemKey={ev.evaluatee_id}>
                                            <CAccordionHeader>
                                                <strong>{ev.evaluatee_name}</strong>
                                            </CAccordionHeader>

                                            <CAccordionBody>
                                                {/* 3 cards before the table */}
                                                <CRow className="g-3 mb-3">
                                                    <CCol md={4}>
                                                        <CCard>
                                                            <CCardHeader>
                                                                <strong>Média ponderada</strong>
                                                            </CCardHeader>
                                                            <CCardBody>
                                                                <div className="fs-4 fw-semibold">{formatScore(weightedAvg)}</div>
                                                            </CCardBody>
                                                        </CCard>
                                                    </CCol>

                                                    <CCol md={4}>
                                                        <CCard>
                                                            <CCardHeader>
                                                                <strong>Objetivos funcionais</strong>
                                                            </CCardHeader>
                                                            <CCardBody>
                                                                <div className="fs-4 fw-semibold">{formatScore(functionals)}</div>
                                                            </CCardBody>
                                                        </CCard>
                                                    </CCol>

                                                    <CCol md={4}>
                                                        <CCard>
                                                            <CCardHeader>
                                                                <strong>Avaliação final</strong>
                                                            </CCardHeader>
                                                            <CCardBody>
                                                                <div className="fs-4 fw-semibold">{formatScore(finalEvaluation)}</div>
                                                            </CCardBody>
                                                        </CCard>
                                                    </CCol>
                                                </CRow>

                                                <CTable bordered small align="middle" responsive striped>
                                                    <CTableHead>
                                                        <CTableRow>
                                                            <CTableHeaderCell>Departamento</CTableHeaderCell>
                                                            <CTableHeaderCell className="text-center" style={{ width: 110 }}>
                                                                Peso
                                                            </CTableHeaderCell>
                                                            <CTableHeaderCell>Avaliadores</CTableHeaderCell>
                                                            <CTableHeaderCell className="text-center">C1</CTableHeaderCell>
                                                            <CTableHeaderCell className="text-center">C2</CTableHeaderCell>
                                                            <CTableHeaderCell className="text-center">C3</CTableHeaderCell>
                                                            <CTableHeaderCell className="text-center">C4</CTableHeaderCell>
                                                            <CTableHeaderCell className="text-center">Nota</CTableHeaderCell>
                                                        </CTableRow>
                                                    </CTableHead>

                                                    <CTableBody>
                                                        {ev.items.map((r) => (
                                                            <CTableRow key={`${ev.evaluatee_id}-${r.department_id}`}>
                                                                <CTableDataCell>
                                                                    {/* Make dept clickable to open the meeting pack for that dept */}
                                                                    <CLink
                                                                        role="button"
                                                                        className="text-decoration-none"
                                                                        onClick={() =>
                                                                            openMeeting({
                                                                                year: r.year,
                                                                                department_id: r.department_id,
                                                                                department_name: r.department_name,
                                                                                evaluatee_id: ev.evaluatee_id,
                                                                                evaluatee_name: ev.evaluatee_name,
                                                                            })
                                                                        }
                                                                    >
                                                                        {r.department_name}
                                                                    </CLink>
                                                                </CTableDataCell>

                                                                <CTableDataCell className="text-center">
                                                                    {formatPercent(r.department_weight)}
                                                                </CTableDataCell>

                                                                <CTableDataCell>
                                                                    {Array.isArray(r.evaluators) && r.evaluators.length > 0 ? (
                                                                        <CListGroup flush>
                                                                            {r.evaluators.map((evaluator) => (
                                                                                <CListGroupItem
                                                                                    key={evaluator.id}
                                                                                    className="d-flex justify-content-between align-items-center py-1"
                                                                                >
                                                                                    <span>{evaluator.name}</span>
                                                                                    {evaluator.concluded ? (
                                                                                        <CBadge color="success">Concluída</CBadge>
                                                                                    ) : (
                                                                                        <CBadge color="warning">Pendente</CBadge>
                                                                                    )}
                                                                                </CListGroupItem>
                                                                            ))}
                                                                        </CListGroup>
                                                                    ) : (
                                                                        <span className="text-muted">—</span>
                                                                    )}
                                                                </CTableDataCell>

                                                                <CTableDataCell className="text-center">
                                                                    {r.complete ? formatScore(r.c1) : '-'}
                                                                </CTableDataCell>
                                                                <CTableDataCell className="text-center">
                                                                    {r.complete ? formatScore(r.c2) : '-'}
                                                                </CTableDataCell>
                                                                <CTableDataCell className="text-center">
                                                                    {r.complete ? formatScore(r.c3) : '-'}
                                                                </CTableDataCell>
                                                                <CTableDataCell className="text-center">
                                                                    {r.complete ? formatScore(r.c4) : '-'}
                                                                </CTableDataCell>

                                                                <CTableDataCell className="text-center">
                                                                    {r.complete ? (
                                                                        formatScore(r.final_score)
                                                                    ) : (
                                                                        <CBadge color="warning">Incompleto</CBadge>
                                                                    )}
                                                                </CTableDataCell>

                                                            </CTableRow>
                                                        ))}
                                                    </CTableBody>
                                                </CTable>
                                            </CAccordionBody>
                                        </CAccordionItem>
                                    );
                                })}
                            </CAccordion>
                        </>
                    )}
                </CCardBody>
            </CCard>
        </>
    );
};

export default ManagerPerformanceOverview;
